<template>
  <v-expansion-panel
    v-if="typeof geometryData !== 'undefined' && geometryData.class === 'flux'"
  >
    <v-expansion-panel-header>Output</v-expansion-panel-header>
    <v-expansion-panel-content>
      <section class="item-body">
        <v-col>
          <v-text-field
            :value="geometryData.name"
            outlined
            dense
            label="Name"
            @change="set('name', $event, false)"
          />
          <v-select
            :value="geometryData.kind"
            :items="kinds"
            outlined
            dense
            label="Output type"
            :hint="kindHint"
            persistent-hint
            @change="set('kind', $event, false)"
          />

          <v-row v-if="geometryData.kind !== 'resonance'" dense class="mt-3">
            <v-col cols="6">
              <v-select
                :value="geometryData.orientation"
                :items="orientations"
                outlined
                dense
                label="Line"
                @change="set('orientation', $event, false)"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                :value="geometryData.length"
                outlined
                dense
                type="number"
                label="Length (µm)"
                hint="Cover the whole beam or waveguide mode"
                persistent-hint
                @change="set('length', Math.abs(Number($event)))"
              />
            </v-col>
          </v-row>

          <div class="text-caption mt-4 mb-1">Frequency band</div>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                :value="centerWavelength"
                outlined
                dense
                type="number"
                label="Center λ (µm)"
                :hint="`fcen = ${Number(geometryData.fcen).toFixed(4)} c/µm`"
                persistent-hint
                @change="setCenterWavelength"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                :value="geometryData.df"
                outlined
                dense
                type="number"
                label="Width Δf (c/µm)"
                :hint="bandHint"
                persistent-hint
                @change="set('df', $event)"
              />
            </v-col>
            <v-col v-if="geometryData.kind !== 'resonance'" cols="6">
              <v-text-field
                :value="geometryData.nfreq"
                outlined
                dense
                type="number"
                label="Points"
                hint="Frequencies in the spectrum"
                persistent-hint
                @change="set('nfreq', Math.max(2, Math.round(Number($event))))"
              />
            </v-col>
          </v-row>

          <v-alert
            dense
            text
            type="info"
            class="mt-4 mb-0 text-caption"
          >
            {{ explanation }}
          </v-alert>
        </v-col>
      </section>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { MONITOR_KINDS } from "@/store/module-simulator/lib/monitors";
import { frequencyToWavelength, wavelengthToFrequency } from "@/store/module-simulator/lib/units";

export default {
  name: "FluxParameters",
  data: () => ({
    kinds: MONITOR_KINDS,
    orientations: [
      { value: "vertical", text: "Vertical (measures flux along x)" },
      { value: "horizontal", text: "Horizontal (measures flux along y)" }
    ]
  }),
  computed: {
    ...mapGetters("simulator", ["geometryData"]),
    centerWavelength() {
      return Number(frequencyToWavelength(Number(this.geometryData.fcen)).toFixed(4));
    },
    bandHint() {
      const fcen = Number(this.geometryData.fcen);
      const df = Number(this.geometryData.df);
      if (!(fcen > 0 && df > 0) || df >= 2 * fcen) return "Must be > 0 and smaller than 2·fcen";
      const longest = frequencyToWavelength(fcen - df / 2);
      const shortest = frequencyToWavelength(fcen + df / 2);
      return `λ from ${shortest.toFixed(3)} to ${longest.toFixed(3)} µm`;
    },
    kindHint() {
      return {
        transmission: "Fraction of the incident power that crosses the line",
        reflection: "Fraction of the incident power that comes back through the line",
        resonance: "Resonant frequencies and quality factor Q measured at this point"
      }[this.geometryData.kind];
    },
    explanation() {
      if (this.geometryData.kind === "resonance") {
        return "Place the point inside the cavity or resonator. After the pulse passes, " +
          "Harminv fits the decaying field and returns each mode's frequency, wavelength and Q.";
      }
      const where = this.geometryData.kind === "reflection"
        ? "between the source and the structure"
        : "after the structure";
      return `Place the line ${where}, away from the PML. The simulator first runs ` +
        "without your geometries to measure the incident power, then with them; the " +
        "spectrum is the ratio of the two (1 = all the power). With one transmission " +
        "and one reflection output, the loss 1 − R − T is also shown.";
    }
  },
  methods: {
    ...mapActions("simulator", ["updateGeometry"]),
    set(field, value, numeric = true) {
      this.updateGeometry([field, numeric ? Number(value) : value]);
    },
    setCenterWavelength(value) {
      this.set("fcen", Number(wavelengthToFrequency(Number(value)).toFixed(6)));
    }
  }
};
</script>
